import { useEffect, lazy, Suspense } from 'react';
import ErrorBoundary from './components/ErrorBoundary'
import LandingPage from './components/LandingPage'
import DataStatusBanner from './components/DataStatusBanner'
import TradingMovedBanner from './components/TradingMovedBanner'
import { TradingProvider } from './context/TradingContext'

// The dashboard (charts, screener, uPlot, Three.js) is the whole app weight.
// A landing visitor never needs it — split it out so `/` loads light.
const MainDashboard = lazy(() => import('./components/MainDashboard'))
const AdminPanel = lazy(() => import('./components/AdminPanel'))
import { DrawingProvider } from './context/DrawingContext'
import { THEME } from './theme/glasswork'
import { loadVenueRegistry, setAssetVenueResolver } from './utils/venueRegistry';
import { loadAssetRegistry, baseVenueOfSymbol } from './utils/assetRegistry';
import { useAuth } from './hooks/useAuth'
import { desktopAuthHandoff } from './utils/desktopAuthHandoff'
import { getToken } from './api/client'
import './App.css'

// ── Spinner shown while the stored JWT is being verified ────────────────────
function BootSpinner() {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(10,20,35,1) 0%, rgba(5,8,16,1) 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20,
    }}>
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
        <circle cx="20" cy="20" r="16" stroke="rgba(44,245,167,0.15)" strokeWidth="3" />
        <path d="M20 4 A16 16 0 0 1 36 20" stroke="#2CF5A7" strokeWidth="3" strokeLinecap="round" />
      </svg>
      <div style={{
        fontFamily: '"JetBrains Mono", monospace', fontSize: 10,
        color: 'rgba(44,245,167,0.5)', letterSpacing: 1.5, textTransform: 'uppercase',
      }}>
        INITIALIZING
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();
  const userId = user?.id ?? 'guest';

  // Fixed 2026-08-10 (found live): a visitor already signed in on this
  // browser who opens `/login?desktop=1&callback=...` (the desktop app's
  // real-browser login flow) used to just see the normal dashboard —
  // `user` truthy meant this component renders MainDashboard directly,
  // AuthModal never mounts, so its handoff-to-desktop-app logic never ran.
  // The desktop app would then sit waiting on its local callback forever
  // ("login failed" after the 5-minute timeout) unless the user logged out
  // and back in just to make AuthModal mount again. Do the same handoff
  // here with whatever token useAuth() already resolved.
  useEffect(() => {
    if (!loading && user) desktopAuthHandoff(getToken());
  }, [loading, user]);

  // Which exchange each ticker trades on. Loaded once for the whole session:
  // order placement, row labels and chart sources all read it, and until it
  // resolves every coin reads as Binance — the app's behaviour before other
  // exchanges existed, not a wrong answer.
  useEffect(() => {
    // The asset registry is the authority on which exchange a symbol's chart
    // comes from; the venue registry defers to it. Injected rather than imported
    // to keep the two modules free of a cycle.
    setAssetVenueResolver(baseVenueOfSymbol);
    loadAssetRegistry();
    loadVenueRegistry();
  }, []);

  // Global data-status banner (Binance server cooldown / per-user 429) — shown
  // over every app state, so it sits outside the auth branches below.
  let content;
  if (loading) {
    content = <BootSpinner />;                                   // verifying JWT
  } else if (!user) {
    content = <LandingPage />;                                   // auth gate
  } else {
    // Billing removed — every signed-in user has full access (no paywall/limits).
    content = (
      <ErrorBoundary>
        <DrawingProvider key={userId}>
          <TradingProvider key={userId}>
            <div className="gw-scope" style={{
              width: '100vw', height: '100vh',
              background: THEME.bgOuter,
              color: THEME.textPrimary,
              fontFamily: THEME.fontUI,
              padding: 0, margin: 0,
            }}>
              <Suspense fallback={<BootSpinner />}>
                <MainDashboard />
              </Suspense>
            </div>
          </TradingProvider>
        </DrawingProvider>
      </ErrorBoundary>
    );
  }

  return (
    <>
      <TradingMovedBanner />
      <DataStatusBanner />
      <Suspense fallback={null}><AdminPanel /></Suspense>
      {content}
    </>
  );
}

export default App
